import { KeyRound, MailPlus, Shield, Trash2, UserPlus, Users } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useAuth } from "../hooks/useAuth";
import { apiRequest } from "../lib/api";
import { betterAuthRequest } from "../lib/auth";
import { displayLoginIdentifier, isLocalLoginIdentifier } from "../lib/local-users";
import type { OrganizationInvitation, OrganizationMember } from "../types";

type Notice = { kind: "success" | "error"; message: string } | null;
type ManagedRole = "owner" | "admin" | "member";

const managedRoleOptions: ManagedRole[] = ["owner", "admin", "member"];

const isManagedRole = (value: string): value is ManagedRole =>
  managedRoleOptions.includes(value as ManagedRole);

const splitRoles = (value: string): string[] =>
  value
    .split(",")
    .map((role) => role.trim())
    .filter(Boolean);

const hasRole = (value: string | null | undefined, role: ManagedRole): boolean =>
  Boolean(value && splitRoles(value).includes(role));

const toTitleCase = (value: string | null | undefined): string => {
  if (!value) {
    return "";
  }

  return value
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

function NoticeBanner({ notice }: { notice: Notice }) {
  if (!notice) {
    return null;
  }

  return (
    <div
      className={`mt-4 rounded-md border px-4 py-3 text-sm ${
        notice.kind === "success"
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-red-200 bg-red-50 text-red-700"
      }`}
    >
      {notice.message}
    </div>
  );
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-5">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {description ? (
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export function UserManagementPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [activeRole, setActiveRole] = useState<string | null>(null);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState<ManagedRole>("member");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<ManagedRole>("member");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  const activeOrganizationId = user?.activeOrganizationId ?? null;
  const activeOrganizationName = user?.activeOrganization?.name ?? null;
  const canManageUsers = hasRole(activeRole, "owner") || hasRole(activeRole, "admin");

  const assignableRoles = useMemo(() => {
    if (hasRole(activeRole, "owner")) {
      return managedRoleOptions;
    }
    return managedRoleOptions.filter((role) => role !== "owner");
  }, [activeRole]);

  const loadUserData = async () => {
    if (!activeOrganizationId) {
      setMembers([]);
      setInvitations([]);
      setActiveRole(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const [membersResult, invitationsResult, roleResult] = await Promise.all([
        betterAuthRequest<{ members: OrganizationMember[]; total: number }>(
          `/organization/list-members?organizationId=${encodeURIComponent(activeOrganizationId)}`,
        ),
        betterAuthRequest<OrganizationInvitation[]>(
          `/organization/list-invitations?organizationId=${encodeURIComponent(activeOrganizationId)}`,
        ),
        betterAuthRequest<{ role: string }>(
          `/organization/get-active-member-role?organizationId=${encodeURIComponent(activeOrganizationId)}`,
        ),
      ]);

      setMembers(membersResult.members ?? []);
      setInvitations(
        (invitationsResult ?? []).filter((invitation) => invitation.status === "pending"),
      );
      setActiveRole(roleResult.role ?? null);
    } catch (error) {
      setNotice({
        kind: "error",
        message: error instanceof Error ? error.message : "Unable to load users.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUserData();
  }, [activeOrganizationId]);

  useEffect(() => {
    if (!assignableRoles.includes(newRole)) {
      setNewRole(assignableRoles[0] ?? "member");
    }

    if (!assignableRoles.includes(inviteRole)) {
      setInviteRole(assignableRoles[0] ?? "member");
    }
  }, [assignableRoles, inviteRole, newRole]);

  const handleCreateUser = async (event: FormEvent) => {
    event.preventDefault();
    setNotice(null);
    setCreating(true);

    try {
      await apiRequest(
        `/api/users?organizationId=${encodeURIComponent(activeOrganizationId ?? "")}`,
        {
          method: "POST",
          body: JSON.stringify({
            username: newUsername,
            password: newPassword,
            role: newRole,
          }),
        },
      );
      setNewUsername("");
      setNewPassword("");
      setNewRole(assignableRoles[0] ?? "member");
      setNotice({ kind: "success", message: "User login created." });
      await loadUserData();
    } catch (error) {
      setNotice({
        kind: "error",
        message: error instanceof Error ? error.message : "Unable to create user.",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async (event: FormEvent) => {
    event.preventDefault();
    setNotice(null);
    setInviting(true);

    try {
      await betterAuthRequest("/organization/invite-member", {
        method: "POST",
        body: JSON.stringify({
          email: inviteEmail.trim().toLowerCase(),
          role: inviteRole,
          organizationId: activeOrganizationId,
        }),
      });
      setInviteEmail("");
      setInviteRole(assignableRoles[0] ?? "member");
      setNotice({ kind: "success", message: "Invitation sent." });
      await loadUserData();
    } catch (error) {
      setNotice({
        kind: "error",
        message: error instanceof Error ? error.message : "Unable to invite user.",
      });
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (memberId: string, role: string) => {
    if (!isManagedRole(role)) {
      return;
    }

    setNotice(null);

    try {
      await betterAuthRequest("/organization/update-member-role", {
        method: "POST",
        body: JSON.stringify({
          memberId,
          role,
          organizationId: activeOrganizationId,
        }),
      });
      setNotice({ kind: "success", message: "Role updated." });
      await loadUserData();
    } catch (error) {
      setNotice({
        kind: "error",
        message: error instanceof Error ? error.message : "Unable to update role.",
      });
    }
  };

  const handleDeleteUser = async (member: OrganizationMember) => {
    const identifier = displayLoginIdentifier(member.user.email);
    const confirmed = window.confirm(`Delete ${identifier}? This removes their login and access.`);
    if (!confirmed) {
      return;
    }

    setNotice(null);

    try {
      await apiRequest(
        `/api/users/${encodeURIComponent(member.user.id)}?organizationId=${encodeURIComponent(activeOrganizationId ?? "")}`,
        {
          method: "DELETE",
        },
      );
      setNotice({ kind: "success", message: "User deleted." });
      await loadUserData();
    } catch (error) {
      setNotice({
        kind: "error",
        message: error instanceof Error ? error.message : "Unable to delete user.",
      });
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    const confirmed = window.confirm("Cancel this invitation?");
    if (!confirmed) {
      return;
    }

    setNotice(null);

    try {
      await betterAuthRequest("/organization/cancel-invitation", {
        method: "POST",
        body: JSON.stringify({ invitationId }),
      });
      setNotice({ kind: "success", message: "Invitation canceled." });
      await loadUserData();
    } catch (error) {
      setNotice({
        kind: "error",
        message: error instanceof Error ? error.message : "Unable to cancel invitation.",
      });
    }
  };

  if (!activeOrganizationId) {
    return (
      <div className="p-4 px-6 pb-10 md:px-16">
        <div className="pt-6 pb-2">
          <h2 className="text-xl tracking-tight">Users</h2>
        </div>
        <SectionCard title="Users" description="Select an organization before managing users.">
          <p className="text-sm text-slate-500">No active organization selected.</p>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="p-4 px-6 pb-10 md:px-16">
      <div className="pt-6 pb-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-500">
          Access Operations
        </div>
        <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">Users</h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Manage logins, roles, and invitations for {activeOrganizationName ?? "this organization"}.
        </p>
      </div>

      <div className="mt-6 space-y-6">
        <SectionCard title="Workspace Access">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                Your role
              </div>
              <div className="mt-2 flex items-center gap-2 text-sm font-medium text-slate-900">
                <Shield className="h-4 w-4 text-slate-500" />
                {activeRole ? toTitleCase(activeRole) : "Member"}
              </div>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                Access control
              </div>
              <div className="mt-2 text-sm font-medium text-slate-900">
                {canManageUsers ? "Can create logins, edit roles, and delete users." : "Read-only access."}
              </div>
            </div>
          </div>
          <NoticeBanner notice={notice} />
        </SectionCard>

        {canManageUsers ? (
          <SectionCard
            title="Create Login"
            description="Create a username and password for immediate dashboard access."
          >
            <form className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_180px_auto]" onSubmit={handleCreateUser}>
              <label className="grid gap-2 text-sm text-slate-700">
                Username
                <input
                  value={newUsername}
                  onChange={(event) => setNewUsername(event.target.value)}
                  className="rounded-md border border-slate-200 px-3 py-2.5 outline-none transition focus:border-slate-900"
                  placeholder="analyst1"
                  required
                />
              </label>
              <label className="grid gap-2 text-sm text-slate-700">
                Password
                <input
                  type="password"
                  value={newPassword}
                  minLength={8}
                  onChange={(event) => setNewPassword(event.target.value)}
                  className="rounded-md border border-slate-200 px-3 py-2.5 outline-none transition focus:border-slate-900"
                  required
                />
              </label>
              <label className="grid gap-2 text-sm text-slate-700">
                Role
                <select
                  value={newRole}
                  onChange={(event) => setNewRole((event.target.value as ManagedRole) ?? "member")}
                  className="rounded-md border border-slate-200 bg-white px-3 py-2.5 outline-none transition focus:border-slate-900"
                >
                  {assignableRoles.map((role) => (
                    <option key={role} value={role}>
                      {toTitleCase(role)}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={creating}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <UserPlus className="h-4 w-4" />
                  {creating ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </SectionCard>
        ) : null}

        {canManageUsers ? (
          <SectionCard title="Invite User" description="Invite a user by email instead of creating a password.">
            <form className="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px_auto]" onSubmit={handleInvite}>
              <label className="grid gap-2 text-sm text-slate-700">
                Email
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  className="rounded-md border border-slate-200 px-3 py-2.5 outline-none transition focus:border-slate-900"
                  placeholder="user@company.com"
                  required
                />
              </label>
              <label className="grid gap-2 text-sm text-slate-700">
                Role
                <select
                  value={inviteRole}
                  onChange={(event) => setInviteRole((event.target.value as ManagedRole) ?? "member")}
                  className="rounded-md border border-slate-200 bg-white px-3 py-2.5 outline-none transition focus:border-slate-900"
                >
                  {assignableRoles.map((role) => (
                    <option key={role} value={role}>
                      {toTitleCase(role)}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={inviting}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <MailPlus className="h-4 w-4" />
                  {inviting ? "Sending..." : "Send invite"}
                </button>
              </div>
            </form>
          </SectionCard>
        ) : null}

        <SectionCard title="Members" description="Current users and their assigned roles.">
          {loading ? (
            <p className="text-sm text-slate-500">Loading members...</p>
          ) : members.length === 0 ? (
            <p className="text-sm text-slate-500">No members found.</p>
          ) : (
            <div className="overflow-hidden rounded-md border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                      Role
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {members.map((member) => {
                    const currentMemberRoles = splitRoles(member.role);
                    const selectedRole = currentMemberRoles.find(isManagedRole) ?? "member";

                    return (
                      <tr key={member.id}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                            {isLocalLoginIdentifier(member.user.email) ? (
                              <KeyRound className="h-4 w-4 text-slate-400" />
                            ) : (
                              <Users className="h-4 w-4 text-slate-400" />
                            )}
                            {member.user.name}
                          </div>
                          <div className="mt-1 text-sm text-slate-500">
                            {displayLoginIdentifier(member.user.email)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {canManageUsers ? (
                            <select
                              value={selectedRole}
                              onChange={(event) => void handleRoleChange(member.id, event.target.value)}
                              className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-900"
                            >
                              {assignableRoles.map((role) => (
                                <option key={role} value={role}>
                                  {toTitleCase(role)}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                              {toTitleCase(member.role)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {canManageUsers ? (
                            <button
                              type="button"
                              onClick={() => void handleDeleteUser(member)}
                              className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 transition hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </button>
                          ) : (
                            <span className="text-sm text-slate-400">No actions</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        <SectionCard title="Pending Invitations" description="Open invitations for this organization.">
          {loading ? (
            <p className="text-sm text-slate-500">Loading invitations...</p>
          ) : invitations.length === 0 ? (
            <p className="text-sm text-slate-500">No pending invitations.</p>
          ) : (
            <div className="space-y-3">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex flex-col gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 md:flex-row md:items-center md:justify-between"
                >
                  <div>
                    <div className="text-sm font-medium text-slate-900">
                      {invitation.email}
                    </div>
                    <div className="mt-1 text-sm text-slate-500">
                      {toTitleCase(invitation.role)} role · invited{" "}
                      {new Date(invitation.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  {canManageUsers ? (
                    <button
                      type="button"
                      onClick={() => void handleCancelInvitation(invitation.id)}
                      className="text-sm font-medium text-red-600 transition hover:text-red-700"
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
